import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSongs } from "../api/songs";
import MusicPlayer from "../components/MusicPlayer";
import Footer from "../components/Footer";

export default function Discography() {
  const [hoveredSong, setHoveredSong] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const audioRef = useRef(null);

  // Fetch songs using TanStack Query with fallback data
  const {
    data: songs = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["songs"],
    queryFn: fetchSongs,
  });

  // Audio playback functions
  const handlePlayPause = (song) => {
    if (currentlyPlaying === song.id && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      if (currentlyPlaying !== song.id) {
        setCurrentlyPlaying(song.id);
        setCurrentTime(0);
        setDuration(0);
        setIsBuffering(true);
        audioRef.current.src = song.audio_file;
        audioRef.current.load();
      }
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsBuffering(false);
    }
  };

  const handleCanPlay = () => {
    setIsBuffering(false);
  };

  const handleWaiting = () => {
    setIsBuffering(true);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  // Scroll animation hook
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in-up");
          entry.target.classList.remove("opacity-30");
        } else {
          entry.target.classList.remove("animate-fade-in-up");
          entry.target.classList.add("opacity-30");
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(".scroll-animate");
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [songs]);

  return (
    <main className="bg-background-dark text-white font-sans overflow-x-hidden">
      <MusicPlayer />

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onWaiting={handleWaiting}
        onEnded={() => {
          setCurrentTime(0);
        }}
      />

      {isLoading && (
        <div className="min-h-screen flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-6"></div>
            <p className="text-white/60 text-sm uppercase tracking-widest">
              Loading Singles...
            </p>
          </div>
        </div>
      )}

      {!isLoading && (
        <section className="max-w-7xl mx-auto px-6 py-32 bg-black">
          <div className="flex flex-col md:flex-row md:items-end justify-end mb-24 space-y-6 md:space-y-0 scroll-animate transition-all duration-700 opacity-30">
            <div>
              <h2
                className="text-5xl font-serif tracking-tight mb-4 text-primary uppercase"
                style={{ textAlign: "right" }}
              >
                Discography
              </h2>
              <p
                className="text-body-grey font-serif italic text-lg opacity-80"
                style={{ textAlign: "right" }}
              >
                Complete Collection of Singles
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-8 px-4">
            {[0, 1].map((colIndex) => (
              <div
                key={colIndex}
                className={`flex flex-col gap-8 w-full sm:w-1/2 ${colIndex === 0 ? "mt-32" : "mt-0"}`}
              >
                {songs
                  .filter((_, i) => i % 2 === colIndex)
                  .map((song, index) => (
                    <div
                      key={song.id}
                      className="group cursor-pointer scroll-animate transition-all duration-700 opacity-30 transform translate-y-8"
                      onMouseEnter={() => setHoveredSong(song.id)}
                      onMouseLeave={() => setHoveredSong(null)}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      <div className="relative aspect-square overflow-hidden mb-3 bg-zinc-950">
                        <img
                          alt={song.title}
                          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                          src={song.img || song.image}
                        />
                        <div
                          className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                            hoveredSong === song.id
                              ? "bg-black/60 backdrop-blur-sm"
                              : "bg-transparent"
                          }`}
                        >
                          <div
                            className={`w-12 h-12 flex items-center justify-center rounded-full bg-white text-black cursor-pointer transition-all duration-300 ${
                              hoveredSong === song.id
                                ? "scale-100 opacity-100"
                                : "scale-75 opacity-0"
                            }`}
                            onClick={() => handlePlayPause(song)}
                          >
                            {isBuffering && currentlyPlaying === song.id ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                            ) : (
                              <span className="material-symbols-outlined text-xl">
                                {currentlyPlaying === song.id && isPlaying
                                  ? "pause"
                                  : "play_arrow"}
                              </span>
                            )}
                          </div>
                        </div>

                        {currentlyPlaying === song.id && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/90 p-3">
                            <div className="flex items-center gap-3 text-xs text-white">
                              <span className="min-w-[35px] text-right">
                                {formatTime(currentTime)}
                              </span>
                              <div className="flex-1 h-2 bg-white/20 rounded-full relative">
                                <div
                                  className="h-full bg-white rounded-full transition-none"
                                  style={{ width: `${progressPercentage}%` }}
                                />
                              </div>
                              <span className="min-w-[35px]">
                                {formatTime(duration)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-serif text-xl font-bold mb-0.5 text-white uppercase tracking-wider">
                          {song.title}
                        </h3>

                        {song.link && (
                          <div
                            className={`flex items-center gap-2 transition-all duration-300 ${
                              hoveredSong === song.id
                                ? "opacity-100"
                                : "opacity-0"
                            }`}
                          >
                            <p className="text-white text-lg tracking-widest">
                              Listen on:{" "}
                            </p>
                            <a
                              href={song.link}
                              target="_blank"
                              className="w-6 h-6 flex items-center justify-center bg-[#1DB954] rounded-full hover:scale-110 transition-transform"
                              title="Spotify"
                            >
                              <svg
                                className="w-3 h-3 text-white"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z" />
                              </svg>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </section>
      )}

      <Footer />

      <style jsx>{`
        .animate-fade-in-up {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </main>
  );
}
